import type { StateCreator } from 'zustand';
import type { AppStore, WordBankSlice, WordBank, Word } from '../types';

const initialWordBankState = {
  wordBanks: [],
  activeWordBankId: null,
  selectedWordIds: [],
  isLoading: false,
  error: null,
};

export const createWordBankSlice: StateCreator<AppStore, [], [], WordBankSlice> = (set) => ({
  ...initialWordBankState,

  setWordBanks: (wordBanks: WordBank[]) =>
    set({ wordBanks }),

  addWordBank: (wordBank: WordBank) =>
    set((state) => ({
      wordBanks: [...state.wordBanks, wordBank],
    })),

  updateWordBank: (id: string, updates: Partial<WordBank>) =>
    set((state) => ({
      wordBanks: state.wordBanks.map((bank) =>
        bank.id === id ? { ...bank, ...updates } : bank
      ),
    })),

  removeWordBank: (id: string) =>
    set((state) => ({
      wordBanks: state.wordBanks.filter((bank) => bank.id !== id),
      activeWordBankId: state.activeWordBankId === id ? null : state.activeWordBankId,
    })),

  setActiveWordBank: (id: string | null) =>
    set({ activeWordBankId: id }),

  addWordToBank: (bankId: string, word: Word) =>
    set((state) => ({
      wordBanks: state.wordBanks.map((bank) =>
        bank.id === bankId
          ? { ...bank, words: [...bank.words, word], updatedAt: new Date().toISOString() }
          : bank
      ),
    })),

  removeWordFromBank: (bankId: string, wordId: string) =>
    set((state) => ({
      wordBanks: state.wordBanks.map((bank) =>
        bank.id === bankId
          ? {
              ...bank,
              words: bank.words.filter((word) => word.id !== wordId),
              updatedAt: new Date().toISOString(),
            }
          : bank
      ),
      selectedWordIds: state.selectedWordIds.filter((id) => id !== wordId),
    })),

  selectWords: (wordIds: string[]) =>
    set({ selectedWordIds: wordIds }),

  toggleWordSelection: (wordId: string) =>
    set((state) => ({
      selectedWordIds: state.selectedWordIds.includes(wordId)
        ? state.selectedWordIds.filter((id) => id !== wordId)
        : [...state.selectedWordIds, wordId],
    })),

  clearWordSelection: () =>
    set({ selectedWordIds: [] }),

  setWordBankLoading: (isLoading: boolean) =>
    set({ isLoading }),

  setWordBankError: (error: string | null) =>
    set({ error }),
});
